/**
 * 3D coverflow carousel for server-rendered cards ([data-wop-coverflow]).
 * The active card faces the viewer; neighbours fan out in perspective.
 * Autoplay pauses on hover/focus and via the pause button (WCAG 2.2.2);
 * with prefers-reduced-motion it advances slower and without 3D turns.
 */

const SWIPE_PX = 40;
const VISIBLE_SIDE = 3;

export function initCoverflows(): void {
    document.querySelectorAll<HTMLElement>('[data-wop-coverflow]').forEach(initCoverflow);
}

function initCoverflow(root: HTMLElement): void {
    const stage = root.querySelector<HTMLElement>('.wop-coverflow__stage');
    const items = Array.from(root.querySelectorAll<HTMLElement>('.wop-coverflow__item'));
    const dots = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-coverflow-dot]'));
    const pauseButton = root.querySelector<HTMLButtonElement>('[data-coverflow-pause]');
    const count = items.length;

    if (!stage || count === 0) {
        return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Number(root.dataset.interval || 3600) * (reduceMotion ? 1.6 : 1);
    let active = 0;
    let timer: number | undefined;
    let hovering = false;
    let focused = false;
    let userPaused = false;

    root.classList.add('is-ready');

    const offsetOf = (index: number) => {
        let offset = (index - active) % count;

        if (offset > count / 2) {
            offset -= count;
        } else if (offset < -count / 2) {
            offset += count;
        }

        return offset;
    };

    const render = () => {
        const spacing = Math.min(250, stage.clientWidth * 0.2);

        items.forEach((item, index) => {
            const offset = offsetOf(index);
            const distance = Math.abs(offset);
            const side = Math.sign(offset);
            const hidden = distance > VISIBLE_SIDE;
            const turn = reduceMotion ? 0 : side * -42;
            const depth = reduceMotion ? 0 : -distance * 140;
            const scale = 1 - Math.min(distance, VISIBLE_SIDE) * (reduceMotion ? 0.12 : 0.06);

            item.style.transform = `translateX(${offset * spacing}px) translateZ(${depth}px) rotateY(${turn}deg) scale(${scale})`;
            item.style.opacity = hidden ? '0' : String(1 - distance * 0.2);
            item.style.zIndex = String(100 - distance);
            item.classList.toggle('is-active', offset === 0);
            item.classList.toggle('is-hidden', hidden);
            item.setAttribute('aria-hidden', String(offset !== 0));

            const link = item.querySelector<HTMLAnchorElement>('a');

            if (link) {
                link.tabIndex = offset === 0 ? 0 : -1;
            }
        });

        dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === active);
            dot.setAttribute('aria-current', String(index === active));
        });
    };

    const goTo = (index: number) => {
        active = ((index % count) + count) % count;
        render();
    };

    const stop = () => {
        window.clearInterval(timer);
        timer = undefined;
    };

    const schedule = () => {
        stop();

        if (!hovering && !focused && !userPaused && count > 1) {
            timer = window.setInterval(() => goTo(active + 1), interval);
        }
    };

    root.querySelector('[data-coverflow-prev]')?.addEventListener('click', () => goTo(active - 1));
    root.querySelector('[data-coverflow-next]')?.addEventListener('click', () => goTo(active + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));

    // A side card is brought to the front instead of being followed.
    items.forEach((item, index) => {
        item.addEventListener('click', (event) => {
            if (index !== active) {
                event.preventDefault();
                goTo(index);
            }
        });
    });

    pauseButton?.addEventListener('click', () => {
        userPaused = !userPaused;
        pauseButton.setAttribute('aria-pressed', String(userPaused));
        pauseButton.setAttribute('aria-label', (userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause) ?? '');
        root.classList.toggle('is-paused', userPaused);
        schedule();
    });

    root.addEventListener('mouseenter', () => {
        hovering = true;
        schedule();
    });
    root.addEventListener('mouseleave', () => {
        hovering = false;
        schedule();
    });
    root.addEventListener('focusin', () => {
        focused = true;
        schedule();
    });
    root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget as Node | null)) {
            focused = false;
            schedule();
        }
    });
    root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(active - 1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(active + 1);
        }
    });

    // Swipe / drag.
    let startX: number | null = null;
    stage.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
    });
    stage.addEventListener('pointerup', (event) => {
        if (startX === null) {
            return;
        }

        const dx = event.clientX - startX;
        startX = null;

        if (Math.abs(dx) > SWIPE_PX) {
            goTo(active + (dx < 0 ? 1 : -1));
        }
    });
    // Suppress the click that ends a swipe.
    stage.addEventListener(
        'click',
        (event) => {
            if (stage.dataset.swiped === '1') {
                event.preventDefault();
                event.stopPropagation();
                delete stage.dataset.swiped;
            }
        },
        true,
    );
    stage.addEventListener('pointermove', (event) => {
        if (startX !== null && Math.abs(event.clientX - startX) > SWIPE_PX) {
            stage.dataset.swiped = '1';
        }
    });

    // Only spin while visible.
    new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) {
            schedule();
        } else {
            stop();
        }
    }).observe(root);

    window.addEventListener('resize', render, { passive: true });
    render();
}
